import { useState, useEffect, useMemo, useCallback } from 'react';
import { ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS, inferAccountType } from '../../constants/accountTypes';
import { isIndividual, isNonProfit, isCorporation } from '../../constants/orgTypes';
import { formatCurrency } from '../../utils/formatting';
import { formatDate, getFiscalYearRange } from '../../utils/date';
import { printSection } from '../../utils/printHelper';
import { ETaxTransferIndividual } from './ETaxTransferIndividual';

// Helper function to format numbers
const formatNumber = (num) => {
    return new Intl.NumberFormat('ja-JP').format(num);
};

const THRESHOLD = 8_000_000;
const computeCorporateTax = (taxableIncome, category) => {
    if (taxableIncome <= 0) return 0;
    if (category === 'normal') {
        return taxableIncome <= THRESHOLD
            ? Math.floor(taxableIncome * 0.15)
            : Math.floor(THRESHOLD * 0.15 + (taxableIncome - THRESHOLD) * 0.232);
    }
    // 非営利型: 法人税法66条3項（収益事業所得のみ、一律19%）
    return Math.floor(taxableIncome * 0.19);
};

export const ETaxTransfer = ({ accounts, journals, setJournals, getAccountBalance, currentFiscalYear, fiscalYears, setFiscalYears, orgType, user, detailSettings, fileSystem, showAlert }) => {
    if (isIndividual(orgType)) {
        return <ETaxTransferIndividual accounts={accounts} journals={journals} setJournals={setJournals} getAccountBalance={getAccountBalance} currentFiscalYear={currentFiscalYear} user={user} fileSystem={fileSystem} showAlert={showAlert} />;
    }

    const [activeTab, setActiveTab] = useState('pl');
    const [categoryFilter, setCategoryFilter] = useState('全体');
    const showCategoryFilter = isNonProfit(orgType);
    const [copyMessage, setCopyMessage] = useState(null);

    // 課税区分（年度ごと）：fiscalYears から読む。なければ 'nonprofit' がデフォルト
    const [taxCategory, setTaxCategory] = useState(() => {
        const fy = fiscalYears?.find(f => f.year === currentFiscalYear);
        return fy?.taxCategory ?? 'nonprofit';
    });

    // マウント時に taxCategory を deductions.json から読み込む
    useEffect(() => {
        if (!fileSystem || !currentFiscalYear) return;
        fileSystem.getDeductions(currentFiscalYear).then(data => {
            if (data?.taxCategory) {
                setTaxCategory(data.taxCategory);
            }
        }).catch(() => {});
    }, [fileSystem, currentFiscalYear]);

    const handleTaxCategoryChange = async (category) => {
        setTaxCategory(category);
        if (setFiscalYears) {
            const updated = fiscalYears.map(fy =>
                fy.year === currentFiscalYear ? { ...fy, taxCategory: category } : fy
            );
            setFiscalYears(updated);
        }
        if (fileSystem && currentFiscalYear) {
            try {
                const existing = await fileSystem.getDeductions(currentFiscalYear) || {};
                await fileSystem.saveDeductions(currentFiscalYear, {
                    ...existing,
                    taxCategory: category,
                });
            } catch (err) {
                console.error('Failed to save taxCategory:', err);
            }
        }
    };

    // 別表四 手入力の加算・減算調整項目
    const [adjustments, setAdjustments] = useState({
        entertainmentNonDeductible: 0,   // 交際費等の損金不算入額
        donationNonDeductible: 0,        // 寄附金の損金不算入額
        excessDepreciation: 0,           // 減価償却超過額
        otherAddition: 0,               // その他加算
        dividendExclusion: 0,            // 受取配当等の益金不算入額
        otherDeduction: 0,              // その他減算
    });
    const updateAdjustment = (key, value) => {
        setAdjustments(prev => ({ ...prev, [key]: Number(value) || 0 }));
    };

    const { start, end } = getFiscalYearRange(currentFiscalYear);

    const copyToClipboard = (value) => {
        const text = String(Math.abs(value));
        navigator.clipboard.writeText(text).then(() => {
            setCopyMessage('コピーしました');
            setTimeout(() => setCopyMessage(null), 1000);
        });
    };

    const CopyableAmount = ({ value, label, isNegative = false }) => (
        <td
            style={{
                textAlign: 'right',
                cursor: 'pointer',
                backgroundColor: '#fffef0',
                fontSize: '14px',
                color: isNegative ? '#e74c3c' : undefined
            }}
            onClick={() => copyToClipboard(value)}
            title="クリックでコピー"
        >
            {isNegative && value !== 0 ? '△' : ''}{formatCurrency(Math.abs(value))}
        </td>
    );

    const getAccountBalanceByCategory = useCallback((accountId, category) => {
        let balance = 0;
        const account = accounts.find(a => a.id === accountId);
        if (!account) return 0;

        (journals || []).forEach(journal => {
            const journalCategory = journal.category || '非収益';
            if (category !== '全体' && journalCategory !== category) return;

            (journal.lines || []).forEach(line => {
                if (line.accountId === accountId) {
                    if (line.type === 'debit') {
                        if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
                            balance += line.amount;
                        } else {
                            balance -= line.amount;
                        }
                    } else {
                        if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
                            balance -= line.amount;
                        } else {
                            balance += line.amount;
                        }
                    }
                }
            });
        });
        return balance;
    }, [accounts, journals]);

    const plData = useMemo(() => {
        const allRevenues = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE)
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter), plSection: a.plSection || 'ordinary' }))
            .filter(a => a.balance !== 0);

        const allExpenses = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE)
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter), plSection: a.plSection || 'ordinary' }))
            .filter(a => a.balance !== 0);

        const revenues = allRevenues.filter(a => a.plSection !== 'extraordinary' && a.plSection !== 'designated');
        const expenses = allExpenses.filter(a => a.plSection !== 'extraordinary' && a.plSection !== 'designated');
        const extraRevenues = allRevenues.filter(a => a.plSection === 'extraordinary');
        const extraExpenses = allExpenses.filter(a => a.plSection === 'extraordinary');

        // 指定正味財産増減の部
        const designatedRevenues = allRevenues.filter(a => a.plSection === 'designated');
        const designatedExpenses = allExpenses.filter(a => a.plSection === 'designated');
        const designatedIncrease = designatedRevenues.reduce((s, a) => s + a.balance, 0)
                                 - designatedExpenses.reduce((s, a) => s + a.balance, 0);

        const totalRevenue = revenues.reduce((sum, a) => sum + a.balance, 0);
        const totalExpense = expenses.reduce((sum, a) => sum + a.balance, 0);
        const ordinaryIncome = totalRevenue - totalExpense;

        const totalExtraRevenue = extraRevenues.reduce((sum, a) => sum + a.balance, 0);
        const totalExtraExpense = extraExpenses.reduce((sum, a) => sum + a.balance, 0);
        const extraordinaryIncome = totalExtraRevenue - totalExtraExpense;

        const netIncome = ordinaryIncome + extraordinaryIncome;

        return { revenues, expenses, totalRevenue, totalExpense, ordinaryIncome, extraRevenues, extraExpenses, totalExtraRevenue, totalExtraExpense, extraordinaryIncome, netIncome, designatedRevenues, designatedExpenses, designatedIncrease };
    }, [accounts, categoryFilter, getAccountBalanceByCategory]);

    const bsData = useMemo(() => {
        const assets = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.ASSET)
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter) }))
            .filter(a => a.balance !== 0);

        const liabilities = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.LIABILITY)
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter) }))
            .filter(a => a.balance !== 0);

        const equityAccounts = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EQUITY)
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter) }));

        const totalAssets = assets.reduce((sum, a) => sum + a.balance, 0);
        const totalLiabilities = liabilities.reduce((sum, a) => sum + a.balance, 0);
        const equityBalance = equityAccounts.reduce((sum, a) => sum + a.balance, 0);
        const netIncome = plData.netIncome;
        const totalEquity = equityBalance + netIncome;

        return { assets, liabilities, equityAccounts, equityBalance, totalAssets, totalLiabilities, totalEquity, netIncome };
    }, [accounts, categoryFilter, getAccountBalanceByCategory, plData.netIncome]);

    // 別表四: 調整後の所得金額
    const beppyo4Data = useMemo(() => {
        const baseIncome = categoryFilter === '収益' ? plData.netIncome : 0;
        const corpTaxAccount = accounts.find(a => a.name === '法人税等');
        const corpTaxExpensed = corpTaxAccount ? getAccountBalanceByCategory(corpTaxAccount.id, categoryFilter) : 0;
        const totalAddition = corpTaxExpensed
            + adjustments.entertainmentNonDeductible
            + adjustments.donationNonDeductible
            + adjustments.excessDepreciation
            + adjustments.otherAddition;
        const totalDeduction = adjustments.dividendExclusion + adjustments.otherDeduction;
        const taxableIncome = baseIncome + totalAddition - totalDeduction;
        return { baseIncome, corpTaxExpensed, totalAddition, totalDeduction, taxableIncome };
    }, [accounts, categoryFilter, plData.netIncome, adjustments, getAccountBalanceByCategory]);

    // 月別推移データ
    const monthlyData = useMemo(() => {
        const currentFY = fiscalYears?.find(fy => fy.year === currentFiscalYear);
        const endMonth = currentFY?.endMonth || 3;
        const startMonth = endMonth === 12 ? 1 : endMonth + 1;
        const startYear = endMonth === 12 ? currentFiscalYear : currentFiscalYear;

        const months = [];
        for (let i = 0; i < 12; i++) {
            const m = ((startMonth - 1 + i) % 12) + 1;
            const y = m >= startMonth || endMonth === 12 ? startYear : startYear + 1;

            let revenue = 0;
            let expense = 0;

            (journals || []).forEach(journal => {
                if (!journal.date) return;
                const jDate = new Date(journal.date);
                const jMonth = jDate.getMonth() + 1;
                const jYear = jDate.getFullYear();
                if (jYear !== y || jMonth !== m) return;

                (journal.lines || []).forEach(line => {
                    const account = accounts.find(a => a.id === line.accountId);
                    if (!account) return;
                    const accountType = inferAccountType(account);
                    if (accountType === ACCOUNT_TYPES.REVENUE) {
                        revenue += line.type === 'credit' ? line.amount : -line.amount;
                    } else if (accountType === ACCOUNT_TYPES.EXPENSE) {
                        expense += line.type === 'debit' ? line.amount : -line.amount;
                    }
                });
            });

            months.push({ year: y, month: m, revenue, expense, profit: revenue - expense });
        }
        return months;
    }, [journals, accounts, currentFiscalYear, fiscalYears]);

    const detailData = useMemo(() => {
        return accounts
            .map(a => ({
                id: a.id,
                code: a.code,
                name: a.name,
                type: a.type,
                typeLabel: ACCOUNT_TYPE_LABELS[a.type],
                balance: getAccountBalanceByCategory(a.id, categoryFilter)
            }))
            .filter(a => a.balance !== 0)
            .sort((a, b) => a.code.localeCompare(b.code));
    }, [accounts, categoryFilter, getAccountBalanceByCategory]);

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '20px'
    };

    const thStyle = {
        backgroundColor: '#f5f6fa',
        padding: '10px',
        textAlign: 'left',
        borderBottom: '2px solid #ddd',
        fontWeight: 'bold'
    };

    const tdStyle = {
        padding: '8px 10px',
        borderBottom: '1px solid #eee'
    };

    const sectionHeaderStyle = {
        backgroundColor: '#e8f4fd',
        padding: '8px 10px',
        fontWeight: 'bold',
        borderBottom: '1px solid #ddd'
    };

    const totalRowStyle = {
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold'
    };

    return (
        <div>
            {copyMessage && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    pointerEvents: 'none'
                }}>
                    <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: '#fff',
                        padding: '20px 40px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}>
                        {copyMessage}
                    </div>
                </div>
            )}

            <div className="page-header">
                <h2>e-Tax転記用</h2>
            </div>

            <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                金額欄をクリックするとクリップボードにコピーされます。e-Taxの入力画面に貼り付けてください。
            </div>

            <div className="card">
                {/* 法人種別選択 */}
                <div style={{marginBottom: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6'}}>
                    <div style={{fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#555'}}>課税区分（年度ごとに選択）</div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer'}}>
                            <input type="radio" value="nonprofit" checked={taxCategory === 'nonprofit'}
                                onChange={() => handleTaxCategoryChange('nonprofit')} style={{marginTop: '3px'}} />
                            <div>
                                <div style={{fontSize: '14px'}}>非営利型（非営利徹底型 / 共益活動型）</div>
                                <div style={{fontSize: '12px', color: '#888'}}>収益事業所得のみ課税 · 法人税法66条3項 · 税率19%</div>
                            </div>
                        </label>
                        <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer'}}>
                            <input type="radio" value="normal" checked={taxCategory === 'normal'}
                                onChange={() => handleTaxCategoryChange('normal')} style={{marginTop: '3px'}} />
                            <div>
                                <div style={{fontSize: '14px'}}>普通型</div>
                                <div style={{fontSize: '12px', color: '#888'}}>全所得課税 · 法人税法66条2項 · 税率15%（800万以下）/ 23.2%（超）</div>
                            </div>
                        </label>
                    </div>
                    <p style={{fontSize: '12px', color: '#e65100', marginTop: '8px', marginBottom: 0}}>役員構成等によって毎期異なります。税理士に確認のうえ選択してください。</p>
                </div>

                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div className="tab-container" style={{ borderBottom: 'none', marginBottom: 0 }}>
                        <div className="tab-list" style={{ flexWrap: 'wrap' }}>
                            <div className={`tab-item ${activeTab === 'pl' ? 'active' : ''}`} onClick={() => setActiveTab('pl')}>
                                正味財産増減計算書
                            </div>
                            <div className={`tab-item ${activeTab === 'bs' ? 'active' : ''}`} onClick={() => setActiveTab('bs')}>
                                貸借対照表
                            </div>
                            <div className={`tab-item ${activeTab === 'beppyo1' ? 'active' : ''}`} onClick={() => setActiveTab('beppyo1')} style={{ backgroundColor: categoryFilter === '収益' ? '#e8f5e9' : undefined }}>
                                別表一
                            </div>
                            <div className={`tab-item ${activeTab === 'beppyo4' ? 'active' : ''}`} onClick={() => setActiveTab('beppyo4')} style={{ backgroundColor: categoryFilter === '収益' ? '#e8f5e9' : undefined }}>
                                別表四
                            </div>
                            <div className={`tab-item ${activeTab === 'beppyo5' ? 'active' : ''}`} onClick={() => setActiveTab('beppyo5')} style={{ backgroundColor: categoryFilter === '収益' ? '#e8f5e9' : undefined }}>
                                別表五
                            </div>
                            <div className={`tab-item ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveTab('monthly')}>
                                月別推移
                            </div>
                            <div className={`tab-item ${activeTab === 'detail' ? 'active' : ''}`} onClick={() => setActiveTab('detail')}>
                                勘定科目内訳書
                            </div>
                        </div>
                    </div>
                    {showCategoryFilter && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                            {['全体', '非収益', '収益', '共通'].map(cat => (
                                <button
                                    key={cat}
                                    className={`btn ${categoryFilter === cat ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setCategoryFilter(cat)}
                                    style={{
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        backgroundColor: categoryFilter === cat
                                            ? (cat === '収益' ? '#2e7d32' : cat === '共通' ? '#e65100' : undefined)
                                            : (cat === '収益' ? '#e8f5e9' : cat === '共通' ? '#fff3e0' : undefined),
                                        borderColor: cat === '収益' ? '#2e7d32' : cat === '共通' ? '#e65100' : undefined,
                                        color: categoryFilter === cat ? '#fff' : (cat === '収益' ? '#2e7d32' : cat === '共通' ? '#e65100' : undefined)
                                    }}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                    {currentFiscalYear}年度（{formatDate(start)}〜{formatDate(end)}）
                    {showCategoryFilter && categoryFilter !== '全体' && (
                        <span style={{
                            marginLeft: '10px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: categoryFilter === '収益' ? '#e8f5e9' : categoryFilter === '共通' ? '#fff3e0' : '#f5f5f5',
                            color: categoryFilter === '収益' ? '#2e7d32' : categoryFilter === '共通' ? '#e65100' : '#666'
                        }}>
                            {categoryFilter}事業のみ
                        </span>
                    )}
                </div>

                {activeTab === 'pl' && (
                    <div id="etax-pl-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            <h3 style={{ margin: 0 }}>正味財産増減計算書</h3>
                            <button className="btn btn-secondary" onClick={() => printSection('etax-pl-section')} style={{ fontSize: '13px', padding: '4px 12px' }}>印刷</button>
                        </div>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: '60%' }}>科目</th>
                                    <th style={{ ...thStyle, width: '20%', textAlign: 'right' }}>金額</th>
                                    <th style={{ ...thStyle, width: '20%', textAlign: 'right' }}>合計</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan="3" style={sectionHeaderStyle}>
                                        I 経常増減の部
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan="3" style={{ ...tdStyle, paddingLeft: '20px', fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                                        1. 経常収益
                                    </td>
                                </tr>
                                {plData.revenues.map(item => (
                                    <tr key={item.id}>
                                        <td style={{ ...tdStyle, paddingLeft: '40px' }}>{item.name}</td>
                                        <CopyableAmount value={item.balance} label={item.name} />
                                        <td style={tdStyle}></td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={{ ...tdStyle, paddingLeft: '30px' }}>経常収益計</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={plData.totalRevenue} label="経常収益計" />
                                </tr>

                                <tr>
                                    <td colSpan="3" style={{ ...tdStyle, paddingLeft: '20px', fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                                        2. 経常費用
                                    </td>
                                </tr>
                                {plData.expenses.map(item => (
                                    <tr key={item.id}>
                                        <td style={{ ...tdStyle, paddingLeft: '40px' }}>{item.name}</td>
                                        <CopyableAmount value={item.balance} label={item.name} />
                                        <td style={tdStyle}></td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={{ ...tdStyle, paddingLeft: '30px' }}>経常費用計</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={plData.totalExpense} label="経常費用計" />
                                </tr>

                                <tr style={{ ...totalRowStyle, backgroundColor: '#e8f4fd' }}>
                                    <td style={{ ...tdStyle, paddingLeft: '20px' }}>当期経常増減額</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={plData.ordinaryIncome} label="当期経常増減額" isNegative={plData.ordinaryIncome < 0} />
                                </tr>

                                <tr>
                                    <td colSpan="3" style={sectionHeaderStyle}>
                                        II 経常外増減の部
                                    </td>
                                </tr>
                                {plData.extraRevenues.length > 0 && (
                                    <>
                                        <tr>
                                            <td colSpan="3" style={{ ...tdStyle, paddingLeft: '20px', fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                                                1. 経常外収益
                                            </td>
                                        </tr>
                                        {plData.extraRevenues.map(item => (
                                            <tr key={item.id}>
                                                <td style={{ ...tdStyle, paddingLeft: '40px' }}>{item.name}</td>
                                                <CopyableAmount value={item.balance} label={item.name} />
                                                <td style={tdStyle}></td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                                {plData.extraExpenses.length > 0 && (
                                    <>
                                        <tr>
                                            <td colSpan="3" style={{ ...tdStyle, paddingLeft: '20px', fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                                                2. 経常外費用
                                            </td>
                                        </tr>
                                        {plData.extraExpenses.map(item => (
                                            <tr key={item.id}>
                                                <td style={{ ...tdStyle, paddingLeft: '40px' }}>{item.name}</td>
                                                <CopyableAmount value={item.balance} label={item.name} />
                                                <td style={tdStyle}></td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                                {plData.extraRevenues.length === 0 && plData.extraExpenses.length === 0 && (
                                    <tr>
                                        <td style={{ ...tdStyle, paddingLeft: '20px', color: '#999' }}>（該当なし）</td>
                                        <td style={tdStyle}></td>
                                        <td style={tdStyle}></td>
                                    </tr>
                                )}
                                <tr style={totalRowStyle}>
                                    <td style={{ ...tdStyle, paddingLeft: '20px' }}>当期経常外増減額</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={plData.extraordinaryIncome} label="当期経常外増減額" isNegative={plData.extraordinaryIncome < 0} />
                                </tr>

                                <tr style={{ ...totalRowStyle, backgroundColor: '#d4edda', fontSize: '16px' }}>
                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>当期一般正味財産増減額</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={plData.netIncome} label="当期一般正味財産増減額" isNegative={plData.netIncome < 0} />
                                </tr>

                                {(taxCategory === 'nonprofit' || plData.designatedRevenues.length > 0 || plData.designatedExpenses.length > 0) && (
                                    <>
                                        <tr>
                                            <td colSpan="3" style={sectionHeaderStyle}>
                                                Ⅱ 指定正味財産増減の部
                                            </td>
                                        </tr>
                                        {plData.designatedRevenues.map(item => (
                                            <tr key={item.id}>
                                                <td style={{ ...tdStyle, paddingLeft: '40px' }}>{item.name}</td>
                                                <CopyableAmount value={item.balance} label={item.name} />
                                                <td style={tdStyle}></td>
                                            </tr>
                                        ))}
                                        {plData.designatedExpenses.map(item => (
                                            <tr key={item.id}>
                                                <td style={{ ...tdStyle, paddingLeft: '40px' }}>{item.name}</td>
                                                <CopyableAmount value={item.balance} label={item.name} />
                                                <td style={tdStyle}></td>
                                            </tr>
                                        ))}
                                        {plData.designatedRevenues.length === 0 && plData.designatedExpenses.length === 0 && (
                                            <tr>
                                                <td style={{ ...tdStyle, paddingLeft: '20px', color: '#999' }}>（該当なし）</td>
                                                <td style={tdStyle}></td>
                                                <td style={tdStyle}></td>
                                            </tr>
                                        )}
                                        <tr style={totalRowStyle}>
                                            <td style={{ ...tdStyle, paddingLeft: '20px' }}>当期指定正味財産増減額</td>
                                            <td style={tdStyle}></td>
                                            <CopyableAmount value={plData.designatedIncrease} label="当期指定正味財産増減額" isNegative={plData.designatedIncrease < 0} />
                                        </tr>
                                        <tr style={{ ...totalRowStyle, backgroundColor: '#c8e6c9', fontSize: '16px' }}>
                                            <td style={{ ...tdStyle, fontWeight: 'bold' }}>正味財産合計増減額</td>
                                            <td style={tdStyle}></td>
                                            <CopyableAmount value={plData.netIncome + plData.designatedIncrease} label="正味財産合計増減額" isNegative={(plData.netIncome + plData.designatedIncrease) < 0} />
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'bs' && (
                    <div id="etax-bs-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            <h3 style={{ margin: 0 }}>貸借対照表</h3>
                            <button className="btn btn-secondary" onClick={() => printSection('etax-bs-section')} style={{ fontSize: '13px', padding: '4px 12px' }}>印刷</button>
                        </div>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: '60%' }}>科目</th>
                                    <th style={{ ...thStyle, width: '20%', textAlign: 'right' }}>金額</th>
                                    <th style={{ ...thStyle, width: '20%', textAlign: 'right' }}>合計</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan="3" style={sectionHeaderStyle}>
                                        資産の部
                                    </td>
                                </tr>
                                {bsData.assets.map(item => (
                                    <tr key={item.id}>
                                        <td style={{ ...tdStyle, paddingLeft: '20px' }}>{item.name}</td>
                                        <CopyableAmount value={item.balance} label={item.name} />
                                        <td style={tdStyle}></td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={tdStyle}>資産合計</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={bsData.totalAssets} label="資産合計" />
                                </tr>

                                <tr>
                                    <td colSpan="3" style={sectionHeaderStyle}>
                                        負債の部
                                    </td>
                                </tr>
                                {bsData.liabilities.map(item => (
                                    <tr key={item.id}>
                                        <td style={{ ...tdStyle, paddingLeft: '20px' }}>{item.name}</td>
                                        <CopyableAmount value={item.balance} label={item.name} />
                                        <td style={tdStyle}></td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={tdStyle}>負債合計</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={bsData.totalLiabilities} label="負債合計" />
                                </tr>

                                <tr>
                                    <td colSpan="3" style={sectionHeaderStyle}>
                                        正味財産の部
                                    </td>
                                </tr>
                                {bsData.equityAccounts.filter(a => a.balance !== 0).map(item => (
                                    <tr key={item.id}>
                                        <td style={{ ...tdStyle, paddingLeft: '20px' }}>{item.name}</td>
                                        <CopyableAmount value={item.balance} label={item.name} />
                                        <td style={tdStyle}></td>
                                    </tr>
                                ))}
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '20px' }}>当期正味財産増減額</td>
                                    <CopyableAmount value={bsData.netIncome} label="当期正味財産増減額" isNegative={bsData.netIncome < 0} />
                                    <td style={tdStyle}></td>
                                </tr>
                                <tr style={{ ...totalRowStyle, backgroundColor: '#d4edda', fontSize: '16px' }}>
                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>正味財産合計</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={bsData.totalEquity} label="正味財産合計" />
                                </tr>

                                <tr style={{ ...totalRowStyle, backgroundColor: '#fff3cd', fontSize: '16px' }}>
                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>負債及び正味財産合計</td>
                                    <td style={tdStyle}></td>
                                    <CopyableAmount value={bsData.totalLiabilities + bsData.totalEquity} label="負債及び正味財産合計" />
                                </tr>
                            </tbody>
                        </table>

                        {bsData.totalAssets !== (bsData.totalLiabilities + bsData.totalEquity) && (
                            <div className="alert alert-error">
                                貸借が一致していません。資産合計: {formatNumber(bsData.totalAssets)} / 負債+正味財産合計: {formatNumber(bsData.totalLiabilities + bsData.totalEquity)}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'beppyo1' && (
                    <div>
                        <h3 style={{ marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            別表一（一）各事業年度の所得に係る申告書
                        </h3>
                        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                            区分を「収益」に設定して収益事業の所得を確認してください。
                        </div>
                        <table style={tableStyle}>
                            <tbody>
                                <tr>
                                    <td style={{ ...tdStyle, width: '50%' }}>1 所得金額又は欠損金額（別表四「52」）</td>
                                    <CopyableAmount value={beppyo4Data.taxableIncome} label="所得金額" isNegative={beppyo4Data.taxableIncome < 0} />
                                </tr>
                                <tr>
                                    <td style={tdStyle}>2 法人税額（1×税率）</td>
                                    <CopyableAmount
                                        value={computeCorporateTax(beppyo4Data.taxableIncome, taxCategory)}
                                        label="法人税額"
                                    />
                                </tr>
                            </tbody>
                        </table>
                        <p style={{ color: '#666', fontSize: '13px', marginTop: '16px' }}>
                            {taxCategory === 'nonprofit'
                                ? '※ 非営利型一般社団法人（法人税法66条3項）: 収益事業所得に一律19%で計算しています。'
                                : '※ 普通型一般社団法人（法人税法66条2項）: 800万円以下15%、超過分23.2%で計算しています。'}
                        </p>
                        <p style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>
                            ※ 租税特措法の15%軽減は時限措置です。適用年度をご確認ください。
                        </p>
                    </div>
                )}

                {activeTab === 'beppyo4' && (() => {
                    const { baseIncome, corpTaxExpensed, totalAddition, totalDeduction, taxableIncome } = beppyo4Data;

                    const inputStyle = {
                        width: '120px', textAlign: 'right', padding: '4px 8px',
                        border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px'
                    };

                    return (
                    <div>
                        <h3 style={{ marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            別表四 所得の金額の計算に関する明細書
                        </h3>
                        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                            法人税等は仕訳から自動取得されます。その他の調整項目は金額を入力してください。
                        </div>
                        <table style={tableStyle}>
                            <tbody>
                                <tr>
                                    <td style={{ ...tdStyle, width: '55%' }}>1 当期利益又は当期欠損の額</td>
                                    <CopyableAmount value={baseIncome} label="当期利益" isNegative={baseIncome < 0} />
                                </tr>

                                {/* 加算 */}
                                <tr>
                                    <td colSpan={2} style={{ ...tdStyle, fontWeight: '600', backgroundColor: '#fff5f5' }}>
                                        加算
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>2 損金経理をした法人税等（自動）</td>
                                    <CopyableAmount value={corpTaxExpensed} label="法人税等" />
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>3 交際費等の損金不算入額</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <input type="number" style={inputStyle} value={adjustments.entertainmentNonDeductible || ''} onChange={e => updateAdjustment('entertainmentNonDeductible', e.target.value)} placeholder="0" />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>4 寄附金の損金不算入額</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <input type="number" style={inputStyle} value={adjustments.donationNonDeductible || ''} onChange={e => updateAdjustment('donationNonDeductible', e.target.value)} placeholder="0" />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>5 減価償却超過額</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <input type="number" style={inputStyle} value={adjustments.excessDepreciation || ''} onChange={e => updateAdjustment('excessDepreciation', e.target.value)} placeholder="0" />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>6 その他の加算項目</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <input type="number" style={inputStyle} value={adjustments.otherAddition || ''} onChange={e => updateAdjustment('otherAddition', e.target.value)} placeholder="0" />
                                    </td>
                                </tr>
                                <tr style={{ backgroundColor: '#fff5f5' }}>
                                    <td style={{ ...tdStyle, paddingLeft: '24px', fontWeight: '600' }}>加算小計</td>
                                    <CopyableAmount value={totalAddition} label="加算合計" />
                                </tr>

                                {/* 減算 */}
                                <tr>
                                    <td colSpan={2} style={{ ...tdStyle, fontWeight: '600', backgroundColor: '#f0fff4' }}>
                                        減算
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>7 受取配当等の益金不算入額</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <input type="number" style={inputStyle} value={adjustments.dividendExclusion || ''} onChange={e => updateAdjustment('dividendExclusion', e.target.value)} placeholder="0" />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...tdStyle, paddingLeft: '24px' }}>8 その他の減算項目</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <input type="number" style={inputStyle} value={adjustments.otherDeduction || ''} onChange={e => updateAdjustment('otherDeduction', e.target.value)} placeholder="0" />
                                    </td>
                                </tr>
                                <tr style={{ backgroundColor: '#f0fff4' }}>
                                    <td style={{ ...tdStyle, paddingLeft: '24px', fontWeight: '600' }}>減算小計</td>
                                    <CopyableAmount value={totalDeduction} label="減算合計" />
                                </tr>

                                {/* 所得金額 */}
                                <tr style={totalRowStyle}>
                                    <td style={tdStyle}>52 所得金額又は欠損金額</td>
                                    <CopyableAmount value={taxableIncome} label="所得金額" isNegative={taxableIncome < 0} />
                                </tr>
                            </tbody>
                        </table>
                        <p style={{ color: '#666', fontSize: '13px', marginTop: '16px' }}>
                            ※ 所得金額 = 当期利益 + 加算 − 減算
                        </p>
                    </div>
                    );
                })()}

                {activeTab === 'beppyo5' && (
                    <div>
                        <h3 style={{ marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            別表五（一）利益積立金額及び資本金等の額の計算に関する明細書
                        </h3>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>区分</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>期首現在利益積立金額</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>当期の増減</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>期末現在利益積立金額</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={tdStyle}>繰越損益金</td>
                                    <CopyableAmount value={bsData.equityBalance} label="期首" />
                                    <CopyableAmount value={plData.netIncome} label="増減" isNegative={plData.netIncome < 0} />
                                    <CopyableAmount value={bsData.totalEquity} label="期末" />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'monthly' && (
                    <div>
                        <h3 style={{ marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            月別収益・費用推移
                        </h3>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>月</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>収益</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>費用</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>差引</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyData.map((m, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle}>{m.year}/{m.month}月</td>
                                        <CopyableAmount value={m.revenue} label={`${m.month}月収益`} />
                                        <CopyableAmount value={m.expense} label={`${m.month}月費用`} />
                                        <CopyableAmount value={m.profit} label={`${m.month}月差引`} isNegative={m.profit < 0} />
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={tdStyle}>合計</td>
                                    <CopyableAmount value={monthlyData.reduce((s, m) => s + m.revenue, 0)} label="収益合計" />
                                    <CopyableAmount value={monthlyData.reduce((s, m) => s + m.expense, 0)} label="費用合計" />
                                    <CopyableAmount value={monthlyData.reduce((s, m) => s + m.profit, 0)} label="差引合計" isNegative={monthlyData.reduce((s, m) => s + m.profit, 0) < 0} />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'detail' && (
                    <div>
                        <h3 style={{ marginBottom: '15px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                            勘定科目内訳書
                        </h3>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>コード</th>
                                    <th style={thStyle}>科目名</th>
                                    <th style={thStyle}>区分</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>残高</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailData.map(item => (
                                    <tr key={item.id}>
                                        <td style={tdStyle}>{item.code}</td>
                                        <td style={tdStyle}>{item.name}</td>
                                        <td style={tdStyle}>{item.typeLabel}</td>
                                        <CopyableAmount value={item.balance} label={item.name} />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ETaxTransfer;
